import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { UsersService } from 'src/app/_services/admin/users.service';
import { JoSchedularService } from 'src/app/_services/jo-schedular.service';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import moment from "moment-timezone";
import { switchMap, of } from 'rxjs';
import { UtilServiceService } from 'src/app/_services/utilService/util-service.service';

@Component({
  selector: 'app-owner-assign',
  templateUrl: './owner-assign.component.html',
  styleUrl: './owner-assign.component.scss',
  standalone: false
})
export class OwnerAssignComponent extends SubscriptionUtil
  implements OnInit, OnDestroy
{
  translate: any;
  data: any;
  assignedToOptions: any = [
    { name: "User", value: "user" },
    { name: "Group", value: "group" },
  ];
  assigneeForm: FormGroup;
  userList: any = [];
  groupList: any = [];
  hideApplyToAllTask: boolean = false;
  orderOwnerUser: any = null;
  installationTime!: any;
  selectedDuration: number = 60;
  minDateForEndDate: Date;             // ✅ added — bound to [minDate] on end calendar
  private skipPillHighlight = false; 
  selectedDate: 'today' | 'tomorrow' | null = null;

  constructor(
    public modalRef: BsModalRef,
    private bsModalServc: BsModalService,
    private userService: UsersService,
    private errorUtil: ErrorUtil,
    private languageTranslateService: LanguageTranslateService,
    private joSchedularService: JoSchedularService,
    private auth: AuthenticationService,
    private util: UtilServiceService
  ) {
    super();
    // Minimal placeholder — ngOnInit rebuilds the real form
    this.assigneeForm = new FormGroup({
      assignTo     : new FormControl(null),
      assignToGroup: new FormControl(null, Validators.required),
      applyToAllTask: new FormControl(false),
    });
  }

  ngOnInit(): void {
    // ── Build form ───────────────────────────────────────────────────────
    this.assigneeForm = new FormGroup({
      assignTo      : new FormControl('user'),
      assignToGroup : new FormControl(null, Validators.required),
      applyToAllTask: new FormControl(true),
      orderDate     : new FormControl(null),
      endDate       : new FormControl(null),
    });

    // ── Cross-field conditional validation (mirrors JO formBuilders) ─────
    // orderDate has value  →  endDate becomes required
    this.assigneeForm.get('orderDate')?.valueChanges.subscribe(val => {
      const endCtrl = this.assigneeForm.get('endDate');
      if (val) {
        endCtrl?.setValidators([Validators.required]);
      } else {
        endCtrl?.clearValidators();
      }
      endCtrl?.updateValueAndValidity({ emitEvent: false });
    });

    // endDate has value  →  orderDate becomes required
    this.assigneeForm.get('endDate')?.valueChanges.subscribe(val => {
      const startCtrl = this.assigneeForm.get('orderDate');
      if (val) {
        startCtrl?.setValidators([Validators.required]);
      } else {
        startCtrl?.clearValidators();
      }
      startCtrl?.updateValueAndValidity({ emitEvent: false });
    });

    // ── Pre-fill assignee ────────────────────────────────────────────────
    if (this.data?.orderOwner) {
      this.assigneeForm.get('assignToGroup')?.setValue(this.data.orderOwner.id);
    }

    // ── Mirror setJobOrderData() date logic (edit JO) ────────────────────
    if (this.data?.installationTime) {
      const rawStart     = this.data.installationTime;
      const datePart     = moment(rawStart).format('YYYY-MM-DD');
      const timePart     = moment(rawStart).format('HH:mm:ss');
      const isoTimestamp = `${datePart}T${timePart}`;
      const startDate    = moment(isoTimestamp).toDate();

      // Stored as moment-tz for the "Current order date" display label
      this.installationTime    = moment.tz(rawStart, this.data.timezone);
      // ✅ mirrors JO: minDateForEndDate = installationDate
      this.minDateForEndDate   = startDate;

      // Triggers the orderDate valueChanges subscriber above →
      // if endDate is also about to be set, orderDate will become required
      this.assigneeForm.get('orderDate')?.setValue(startDate);

      if (this.data?.endDateTime) {
        // mirrors JO: endDateTime = moment(endDateTime).toDate()
        const endDate = moment(this.data.endDateTime).toDate();
        this.assigneeForm.get('endDate')?.setValue(endDate);

        // mirrors JO calculateEstimatedDuration(): diff in minutes
        const diffMinutes = moment(endDate).diff(moment(startDate), 'minutes');
        if (diffMinutes > 0) {
          this.selectedDuration = diffMinutes;
        }
        this.data.estimatedCompletionDuration = this.selectedDuration;
      }
    }

    this.getLanguageData();
  }

  // ── Called when start date picker fires (onSelect) ──────────────────────
  // mirrors JO onOrderDate(): updates minDateForEndDate then recalculates end
  onOrderDateChange(date: Date): void {
    if (!date) return;
    // ✅ mirrors JO: minDateForEndDate = newDate
    this.minDateForEndDate = date;
    // Recalculate end date using whatever chip is currently active
    this.setDuration(this.selectedDuration ?? 60);
  }

  // ── Called when end date picker fires (onSelect / ngModelChange) ─────────
  // mirrors JO onEndDateChange() + calculateEstimatedDuration()
  onEndDateChange(date: Date): void {
    if (!date) return;

    // Keep data.endDateTime in sync for schedulePayload
    this.data.endDateTime = date;

    // mirrors JO calculateEstimatedDuration()
    const startDate = this.assigneeForm.get('orderDate')?.value;
    if (!startDate) return;

    const start = moment(startDate);
    const end   = moment(date);
    if (!start.isValid() || !end.isValid()) return;

    const diffMinutes = end.diff(start, 'minutes');
    if (diffMinutes > 0) {
      // Update selectedDuration so the matching chip highlights
      this.selectedDuration                  = diffMinutes;
      this.data.estimatedCompletionDuration  = diffMinutes;
    }
  }

  // ── Duration chip clicked ────────────────────────────────────────────────
  // sets selectedDuration AND recomputes endDate from current start
 // ✅ Optional startDate param — avoids re-reading form control mid-change-cycle
setDuration(minutes: number, startDate?: Date): void {
  if (!this.skipPillHighlight) {
    this.selectedDuration = minutes;
    this.selectedDate = null; // 👈 clears Today/Tomorrow highlight when pill clicked
  }

  const start: Date | null = startDate ?? this.assigneeForm.get('orderDate')?.value;
  if (!start) return;

  const endDate = moment(start).add(minutes, 'minutes').toDate();

  // ✅ Push outside current CD cycle — fixes PrimeNG calendar not reflecting
  // programmatic setValue when called inside a reactive valueChanges chain
  setTimeout(() => {
    this.assigneeForm.get('endDate')?.setValue(endDate, { emitEvent: false });
    this.data.endDateTime                 = endDate;
    this.data.estimatedCompletionDuration = minutes;
  }, 0);
  console.log("Start date:", start, "Duration set to", minutes, "minutes. Computed endDate:", endDate);
}

  // ── Today / Tomorrow shortcuts ───────────────────────────────────────────
  setDate(when: 'today' | 'tomorrow'): void {
  const date = new Date();
  if (when === 'tomorrow') {
    date.setDate(date.getDate() + 1);
    date.setHours(9, 0, 0, 0);
  }

  this.selectedDate = when;       // 👈 highlights Today/Tomorrow pill
  this.selectedDuration = null; // 👈 clear before chain
  this.skipPillHighlight = true;

  // ✅ Reset duration to 1 hr for Today/Tomorrow shortcuts
  //   this.selectedDuration = 60;

   // 👇 set flag before triggering the chain
  this.skipPillHighlight = true;

  this.assigneeForm.get('orderDate')?.setValue(date);
  this.onOrderDateChange(date); // calls setDuration(60) → end = start + 1hr
  // 👇 reset flag after chain completes
  setTimeout(() => {
    this.skipPillHighlight = false;
    this.selectedDuration = null; // 👈 force clear after chain fully completes
  }, 50); // slightly longer than the 0ms setTimeout in setDuration
}

  // ── Save ─────────────────────────────────────────────────────────────────
  onConfirmClick(): void {
    // mirrors JO onSubmit: markAllAsTouched + warn toast on invalid
    if (this.assigneeForm.invalid) {
      for (const ctrl in this.assigneeForm.controls) {
        this.assigneeForm.controls[ctrl].markAsTouched();
      }
      this.errorUtil.setErrorMessage(
        '400', null,
        this.translate?.general.messages.fillAllRequiredFields,
        'warn', 2000
      );
      return;
    }

    const formVal   = this.assigneeForm.value;
    const isUser    = formVal.assignTo === 'user';
    const orderDate = formVal.orderDate;
    const endDate   = formVal.endDate;

    const assignPayload = {
      jobOrderId: this.data.id,
      userId: isUser ? formVal.assignToGroup : null,
      userGroupId: !isUser ? formVal.assignToGroup : null,
      applyToAllTasks: formVal.applyToAllTask,
      applyProfileBasedFilter: true
    };

    const originalDate = this.installationTime ? moment(this.installationTime) : null;

   // const newDate = orderDate ? moment(orderDate) : null;


    
    const dateChanged = !!orderDate

    const schedulePayload: any = {
      jobOrderId      : this.data.id,
      user            : this.data.orderOwner ?? null,
      installationDate: orderDate ?? null,
      installationTime:orderDate,
      endDateTime: endDate ?? null,
      estimatedDuration: this.data.estimatedCompletionDuration ?? null,
    };


    // Convert installation time to user's timezone
              if (schedulePayload.installationDate) {
                          const tempTime = schedulePayload.installationTime;
                          const date = moment(schedulePayload.installationDate).format("YYYY-MM-DD");
                          let isoTimestamp: any;
                          if (tempTime) { isoTimestamp = `${date}T${moment(tempTime).format("HH:mm:ss")}`; }
                          else { isoTimestamp = `${date}T00:00:00`; }
                          schedulePayload.installationDate = moment(isoTimestamp).toDate();
                 }
                  if (schedulePayload.installationTime) {
                      schedulePayload.installationTime = moment.tz(schedulePayload.installationTime, this.auth.getUserTimezone());
                 }
       

                  if (schedulePayload.endDateTime && schedulePayload.endDateTime != "") {
                       let endDateTime = this.util.timeFormatter(
                         moment(schedulePayload.endDateTime).format("h:mm A"),
                         moment(schedulePayload.endDateTime, "YYYY-MM-DD").format(
                           "YYYY-MM-DD"
                         )
                       );
                       schedulePayload.endDateTime = moment.tz(schedulePayload.endDateTime, this.auth.getUserTimezone());
                     } else {
                       schedulePayload.endDateTime = null;
                     }


    
    if (!schedulePayload.user) delete schedulePayload.user;

    this.joSchedularService.jobOrderReAssign(assignPayload, dateChanged).pipe(
      switchMap((res: any) => {
        if (dateChanged && schedulePayload.installationTime) {
          return this.joSchedularService.jobOrderReSchedule(schedulePayload);
        }
        return of(res);
      })
    ).subscribe({
      next: (mess: any) => {
        this.errorUtil.setErrorMessage(200, null, mess?.body?.message || 'scheduled successfully', 'success', 1000);
        this.modalRef.hide();
      },
      error: (err: any) => {
        this.errorUtil.setErrorMessage(  err.status, null, err.error?.title, 'error', 3000  );
      }
    });
  }

  // ── Language ─────────────────────────────────────────────────────────────
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          this.getUsersList();
          this.getGroupsList();
        }
      })
    );
  }

  push(obs: any) {
    super.push(obs);
  }

  // ── Users list ───────────────────────────────────────────────────────────
  getUsersList() {
    this.userService.getActiveUsers().subscribe(
      (res: any) => {
        this.userList = res;
        if (!this.data.hideOrderOwnerOption) {
          this.orderOwnerUser = this.userList.filter(
            (user: any) => user.id == this.auth.getUserId()
          );
        }
      },
      (err: any) => {
        this.errorUtil.setErrorMessage(
          err.status,
          err.error ? err.error.title : null,
          err.error ? err.error.title : null,
          'error', 2000
        );
      }
    );
  }

  // ── Groups list ──────────────────────────────────────────────────────────
  getGroupsList() {
    this.userService.getAlluserGroup().subscribe(
      (res: any) => {
        this.groupList = res;
      },
      (err: any) => {
        this.errorUtil.setErrorMessage(
          err.status,
          err.error ? err.error.title : null,
          err.error ? err.error.title : null,
          'error', 2000
        );
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}